export const CHECKOUT_URL = 'https://ortho-en.impultienda.ar/checkout/';

export const images = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/79b1d63a-0c10-42fc-ac0e-054fa0411645.jpg',
  doctor: 'https://cdn.magicpatterns.com/uploads/fMXJi2arSXzX8C5cihdG1S/DR._NATHAN_BROOKS_-_ORTHO.png',
};

export const beforeItems = [
  'You look at a complicated case and aren’t sure where to start.',
  'You remember several possible mechanics but aren’t sure which problem should be addressed first.',
  'You jump between notes, courses and references before making a plan.',
  'You focus on the appliance before defining the clinical problem.',
  'You recognize the problem but aren’t sure what needs to be protected.',
  'You reach the middle of treatment and realize a previous decision needs to be reconsidered.',
];

export const afterItems = [
  'You start every case with the same structured sequence.',
  'You identify the dominant problem before thinking about mechanics.',
  'You define the treatment objective before choosing the movement.',
  'You identify what needs to be controlled or protected.',
  'You anticipate the main risk before committing to a strategy.',
  'You establish checkpoints before moving to the next phase.',
];

export const concepts = [
  'Diagnosis',
  'Anchorage',
  'Biomechanics',
  'Alignment',
  'Leveling',
  'Retraction',
  'Torque',
  'Finishing',
];

export const painPoints = [
  {
    icon: 'help',
    text: 'You have a case in front of you and know several possible approaches, but you’re not sure which decision should come first.',
  },
  {
    icon: 'clock',
    text: 'You spend too much time reviewing notes and different resources before planning a case.',
  },
  {
    icon: 'route',
    text: 'You sometimes know what you want to achieve but aren’t completely sure how to sequence the decisions.',
  },
  {
    icon: 'wrench',
    text: 'You find yourself thinking about brackets, wires or mechanics before fully defining the clinical problem.',
  },
  {
    icon: 'refresh',
    text: 'A case isn’t progressing as expected and you need a structured way to review what may have gone wrong.',
  },
  {
    icon: 'puzzle',
    text: 'You understand orthodontic concepts individually, but putting them together into one coherent treatment strategy still feels harder than it should.',
  },
];

export const volumes = [
  {
    number: 'I',
    title: 'The Ortho Case Decoder™',
    tagline: 'Stop looking at a case as a collection of teeth. Start seeing the clinical problem.',
    description:
      'The first step of the system: how to break a case down before deciding on mechanics. Includes the 7-Question Case Check™, the framework used throughout the entire system.',
    points: [
      'What you see',
      'What it means',
      'What matters most',
      'What needs to be protected',
      'What information is still missing',
      'What the treatment objective should be',
    ],
  },
  {
    number: 'II',
    title: 'The Diagnosis Decision Map™',
    tagline: 'Know what you need to decide before you decide how to treat.',
    description:
      'Instead of collecting information randomly, you’ll know what decision each piece of information is supposed to help you make.',
    points: [
      'Patient information',
      'Clinical findings',
      'Skeletal considerations',
      'Dental relationships',
      'Transverse, vertical & AP considerations',
      'Critical questions before treatment',
    ],
  },
  {
    number: 'III',
    title: 'The Control & Anchorage System™',
    tagline: 'Before you move something, know what you cannot afford to lose control of.',
    description:
      'The goal isn’t to memorize more mechanics. It’s to understand what your mechanics are supposed to accomplish and what they need to control.',
    points: [
      'Movement objectives',
      'Anchorage demands',
      'Unwanted movement',
      'Control points',
      'Risk identification',
      'Decision checkpoints',
    ],
  },
  {
    number: 'IV',
    title: 'The Alignment & Leveling Decision System™',
    tagline: 'Stop thinking “Which wire next?” and start thinking “What is this phase supposed to accomplish?”',
    description:
      'Every stage is connected to a clinical objective, not simply a sequence of appliances.',
    points: [
      'Phase objectives',
      'Arch coordination',
      'Movement priorities',
      'Progress checkpoints',
      'Common decision traps',
      'When to reassess the plan',
    ],
  },
  {
    number: 'V',
    title: 'The Retraction & Space-Closure Map™',
    tagline: 'Know what you’re trying to control before you start closing space.',
    description:
      'Replace “I think this is the next step” with “This is the objective, this is the risk, and this is what I need to monitor.”',
    points: [
      'Space analysis',
      'Anchorage considerations',
      'Anterior control',
      'Torque considerations',
      'Common complications',
      'When to reassess',
    ],
  },
  {
    number: 'VI',
    title: 'The Finishing & Case Review System™',
    tagline: 'Don’t call a case finished just because the teeth look straight.',
    description:
      'Includes the Case Review Audit™ to help you systematically review a case that isn’t progressing as expected.',
    points: [
      'Occlusal relationships',
      'Contacts & torque',
      'Settling & stability',
      'Remaining discrepancies',
      'Final case review',
      'Post-treatment considerations',
    ],
  },
];

export const toolkit = [
  {
    icon: 'clipboard',
    title: '7-Question Case Check™',
    text: 'A one-page framework for structuring your initial case reasoning.',
  },
  {
    icon: 'list',
    title: 'Decision Checklists',
    text: 'Checklists for the major stages of treatment.',
  },
  {
    icon: 'map',
    title: 'Clinical Decision Maps',
    text: 'Visual flowcharts: Finding → Interpretation → Objective → Decision.',
  },
  {
    icon: 'file',
    title: 'Case Analysis Worksheets',
    text: 'Structured pages for documenting your reasoning before committing to a strategy.',
  },
  {
    icon: 'filter',
    title: 'Risk & Control Filters',
    text: 'Simple visual tools for identifying what needs attention before moving forward.',
  },
  {
    icon: 'search',
    title: 'Case Review Audit™',
    text: 'A structured review process for cases that aren’t progressing as expected.',
  },
];

export const sevenQuestions = [
  'What is the problem?',
  'What is the objective?',
  'What needs to move?',
  'What needs to be controlled?',
  'What is the main risk?',
  'How will I know the strategy is working?',
  'What must happen before I move to the next phase?',
];

export const bonuses = [
  {
    number: 1,
    title: 'Ortho Case Decoder — 20 Practice Cases',
    value: 47,
    image: 'https://cdn.magicpatterns.com/uploads/hB7aipnUQyTPo3EASsCvCb/BONUS_1_ORTHO-EN.png',
    description:
      'Twenty educational case scenarios designed to help you practice the decision framework. This turns the framework into something you can actually practice.',
    points: [
      'What do you see?',
      'What is the dominant problem?',
      'What is the objective?',
      'What needs to be controlled?',
      'What would you review before proceeding?',
    ],
  },
  {
    number: 2,
    title: 'The 15-Minute Case Analysis Protocol™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/mKcYXxBaozK1QadLBSR1mi/BONUS_2_ORTHO-EN.png',
    description:
      'A condensed version of the entire system — a printable worksheet you can keep with your case documentation: Observe → Define → Prioritize → Plan → Control → Check → Decide.',
    points: [
      'Use it when you need to organize your thoughts quickly',
      'Printable and reusable',
      'Mirrors the full 7-step framework',
    ],
  },
  {
    number: 3,
    title: 'Ortho Error & Decision Trap Library',
    value: 39,
    image: 'https://cdn.magicpatterns.com/uploads/u91qA48uSwRWbZgYdmaAT5/BONUS_3_ORTHO-EN.png',
    description:
      'Common reasoning traps that make cases harder to manage. It trains you to notice the decision that needs to be reconsidered.',
    points: [
      'Treating the appliance instead of the problem',
      'Moving too quickly to mechanics',
      'Ignoring control requirements',
      'Failing to define a checkpoint',
      'Confusing alignment with completion',
    ],
  },
  {
    number: 4,
    title: '30 ChatGPT Prompts for Orthodontic Study',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/1bgXctQYieJ7Y14HESnQYP/BONUS_4_-_ORTHO-EN.png',
    description:
      'Curated prompts designed as educational support — not as a substitute for professional diagnosis or patient-specific clinical judgment.',
    points: [
      'Quiz yourself on orthodontic concepts',
      'Create case-based questions',
      'Challenge your treatment reasoning',
      'Simulate case discussions',
      'Build personalized study exercises',
    ],
  },
  {
    number: 5,
    title: 'The 30-Point Chair-Side Checklist™',
    value: 19,
    image: 'https://cdn.magicpatterns.com/uploads/r9oe3hZq7qC9Po4swWNzWb/BONUS_5_ORTHO-EN.png',
    description:
      'A printable quick-reference checklist. Simple, visual, practical — designed to be used repeatedly.',
    points: [
      'Before starting treatment',
      'Before changing strategy',
      'Before progressing to another phase',
      'Before finishing a case',
    ],
  },
];

export const offerStack = [
  { label: 'Ortho Decision System™ — 6 Premium Volumes', value: 97 },
  { label: 'Bonus #1 — 20 Practice Cases', value: 47 },
  { label: 'Bonus #2 — 15-Minute Case Analysis Protocol™', value: 37 },
  { label: 'Bonus #3 — Error & Decision Trap Library', value: 39 },
  { label: 'Bonus #4 — 30 ChatGPT Prompts', value: 29 },
  { label: 'Bonus #5 — 30-Point Chair-Side Checklist™', value: 19 },
];

export const offerPerks = [
  'Lifetime access',
  'Instant digital delivery',
  'Printable decision tools',
  'No monthly payments',
];

export const testimonials = [
  {
    name: 'Dr. Camila Reyes',
    role: 'General Dentist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a02dca91-a3e7-45c2-8c08-c2c334943a50.jpg',
    quote:
      'I finally have an order to follow. I start with the dominant problem, write the objective, and only then think about mechanics. My case analysis feels organized for the first time.',
  },
  {
    name: 'Dr. Michael Grant',
    role: 'Orthodontist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7d22b4fa-28c0-459a-920d-b93e88c03588.jpg',
    quote:
      'I used to open five different notes before planning a case. Now I go through the 7 questions and I’m done in a fraction of the time.',
  },
  {
    name: 'Dr. Hana Kim',
    role: 'Orthodontic Resident',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3360ea92-a986-4b7d-9418-556f421e721d.jpg',
    quote:
      'The decision maps and checklists are what I use the most. They’re printed and sitting next to my case documentation.',
  },
  {
    name: 'Dr. Andre Williams',
    role: 'General Dentist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/f0671a69-ff64-41ab-883e-deac6fbe5660.jpg',
    quote:
      'I had a case that stalled during space closure. The Case Review Audit helped me see which earlier decision I needed to revisit.',
  },
  {
    name: 'Dr. Sarah Jenkins',
    role: 'Orthodontist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/777e6a71-ccda-451a-ac39-8e0cbdd10543.jpg',
    quote:
      'It’s not a course I watched once and forgot. It’s a reference I come back to every time I plan or reassess a case.',
  },
];

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: 'Immediately after your purchase, you’ll receive access instructions for the digital Ortho Decision System™ and all five bonuses.',
  },
  {
    q: 'Is this a course?',
    a: 'No. The Ortho Decision System™ is a digital decision toolkit made up of six premium volumes, decision maps, worksheets, checklists and practical resources. It’s designed to be consulted repeatedly rather than watched from beginning to end like a traditional course.',
  },
  {
    q: 'Do I need to be an orthodontic specialist?',
    a: 'The system is designed for dental professionals who already have relevant orthodontic education and want a more structured framework for organizing their case reasoning. It is not intended to replace formal specialist training, supervision or patient-specific clinical judgment.',
  },
  {
    q: 'Does this replace clinical training?',
    a: 'No. The Ortho Decision System™ is an educational and organizational resource. Clinical decisions must always be based on the individual patient, appropriate examination, records, professional training and clinical judgment.',
  },
  {
    q: 'Can I print the materials?',
    a: 'Yes. The worksheets and checklists are designed to be printable and reusable.',
  },
  {
    q: 'Is this a monthly subscription?',
    a: 'No. It’s a one-time payment of $27. You receive lifetime access to the materials included with your purchase.',
  },
  {
    q: 'What if I can’t finish everything immediately?',
    a: 'That’s exactly why the system is designed as a reference toolkit. Start with the 15-Minute Case Analysis Protocol™, then return to the relevant volume whenever you need deeper guidance.',
  },
  {
    q: 'What if I don’t think it’s right for me?',
    a: 'You have a 7-day guarantee. Review the material, explore the tools and decide whether it fits the way you want to organize your case reasoning. If it doesn’t, follow the refund process within the guarantee period.',
  },
];

export const recentBuyers = [
  { name: 'María G.', place: 'Mexico City, Mexico' },
  { name: 'Lucía R.', place: 'Bogotá, Colombia' },
  { name: 'Javier M.', place: 'Madrid, Spain' },
  { name: 'Valentina S.', place: 'Buenos Aires, Argentina' },
  { name: 'Diego F.', place: 'Santiago, Chile' },
  { name: 'Camila T.', place: 'Lima, Peru' },
  { name: 'Andrés P.', place: 'Quito, Ecuador' },
  { name: 'Sofía L.', place: 'Montevideo, Uruguay' },
  { name: 'Carlos V.', place: 'San José, Costa Rica' },
  { name: 'Daniela H.', place: 'Santo Domingo, Dominican Republic' },
  { name: 'Mateo C.', place: 'Asunción, Paraguay' },
  { name: 'Gabriela N.', place: 'Panama City, Panama' },
  { name: 'Fernando A.', place: 'Guadalajara, Mexico' },
  { name: 'Isabella Q.', place: 'San Juan, Puerto Rico' },
];
